import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

interface DashboardStats {
  citasHoy: number;
  citasMes: number;
  trabajadores: number;
  serviciosDisponibles: number;
  facturadoMes: number;
  clientesUnicos: number;
  cancelacionesMes: number;

  bonosVendidosMes: number;
  ingresosBonosMes: number;
  usosBonosMes: number;
  bonosDisponibles: number;

  citasMesVsMesPasado: number | null;
  facturadoMesVsMesPasado: number | null;
  clientesUnicosVsSemanaPasada: number | null;
  cancelacionesMesVsMesPasado: number | null;
  ingresosBonosMesVsMesPasado: number | null;

  gastosMes: number;
  gastosMesVsMesPasado: number | null;
  beneficioMes: number;
  margenMes: number | null;
}

interface CitasSemanaData {
  fecha: string;
  dia: string;
  total_citas: number;
}

interface ServiciosMesData {
  servicio: string;
  total_citas: number;
  total_ingresos: number;
}

interface DashboardResponse {
  stats: DashboardStats;
  citasSemana: CitasSemanaData[];
  serviciosMes: ServiciosMesData[];
}

const STATS_INICIALES: DashboardStats = {
  citasHoy: 0,
  citasMes: 0,
  trabajadores: 0,
  serviciosDisponibles: 0,
  facturadoMes: 0,
  clientesUnicos: 0,
  cancelacionesMes: 0,

  bonosVendidosMes: 0,
  ingresosBonosMes: 0,
  usosBonosMes: 0,
  bonosDisponibles: 0,

  citasMesVsMesPasado: null,
  facturadoMesVsMesPasado: null,
  clientesUnicosVsSemanaPasada: null,
  cancelacionesMesVsMesPasado: null,
  ingresosBonosMesVsMesPasado: null,

  gastosMes: 0,
  gastosMesVsMesPasado: null,
  beneficioMes: 0,
  margenMes: null,
};

export const useDashboard = (
  empresaId: string,
  mes?: number,
  anio?: number
) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>(STATS_INICIALES);
  const [citasSemana, setCitasSemana] = useState<CitasSemanaData[]>([]);
  const [serviciosMes, setServiciosMes] = useState<ServiciosMesData[]>([]);

  const mesFiltro = mes ?? new Date().getMonth() + 1;
  const anioFiltro = anio ?? new Date().getFullYear();

  useEffect(() => {
    if (!empresaId) return;

    const load = async () => {
      setLoading(true);

      const { data, error } = await supabase.rpc('get_dashboard', {
        p_empresa: empresaId,
        p_mes: mesFiltro,
        p_anio: anioFiltro,
      });

      if (error) {
        Sentry.captureException(error, {
          // 👈
          extra: { empresaId, mesFiltro, anioFiltro },
        });
        setLoading(false);
        return;
      }

      const res = data as DashboardResponse;

      setStats(res.stats);
      setCitasSemana(res.citasSemana ?? []);
      setServiciosMes(res.serviciosMes ?? []);
      setLoading(false);
    };

    load();
  }, [empresaId, mesFiltro, anioFiltro]);

  return {
    loading,
    stats,
    citasSemana,
    serviciosMes,
  };
};
